import { ColumnModel } from "app/services/api/types/common/models/ColumnModel";
import { ScrollView, Text, View } from "react-native";
import LoadingSpinner from "ui/atoms/spinner/LoadingSpinner";
import { useScreenSize } from "ui/utils/ScreenSize";
import { TableHeader } from "../molecules/TableHeader";
import { TableRow } from "../molecules/TableRow";

interface Props<T> {
  data?: T[];
  columns: ColumnModel<T>[];
  isFetching?: boolean;
  emptyMessage?: string;
  rowClassName?: string;
  borderClassName?: string;
  mobileRenderItem?: (item: T, index: number) => React.ReactNode;
  clickablePath?: (item: T) => string;
  onRowPress?: (item: T) => void;
  showHorizontalRowDivider?: boolean;
  showHorizontalHeaderDivider?: boolean;
}

export default function TableList<T>({
  data,
  columns = [],
  mobileRenderItem,
  isFetching,
  emptyMessage,
  rowClassName,
  borderClassName = "border border-gray-300 rounded-xl",
  clickablePath,
  onRowPress,
  showHorizontalRowDivider,
  showHorizontalHeaderDivider,
}: Props<T>) {
  const { isMobile } = useScreenSize();

  return (
    <>
      {isMobile && mobileRenderItem ? (
        <ScrollView>
          <View className="space-y-4">
            {isFetching ? (
              <View className="flex-1 justify-center items-center py-10 bg-white">
                <LoadingSpinner />
              </View>
            ) : data?.length ? (
              data.map((item, index) => mobileRenderItem?.(item, index))
            ) : (
              <Text className="text-center text-gray-500">
                {emptyMessage ?? "No data"}
              </Text>
            )}
          </View>
        </ScrollView>
      ) : (
        <View className={`flex-1 overflow-hidden ${borderClassName}`}>
          <ScrollView
            horizontal
            showsHorizontalScrollIndicator
            contentContainerClassName="min-w-full"
          >
            <View style={{ minWidth: "100%" }}>
              <TableHeader
                columns={columns}
                showHorizontalHeaderDivider={showHorizontalHeaderDivider}
              />
              {isFetching ? (
                <View className="flex-1 justify-center items-center py-10 bg-white">
                  <LoadingSpinner />
                </View>
              ) : (
                <ScrollView contentContainerClassName="pb-9">
                  {data?.length ? (
                    data.map((item, index) => (
                      <TableRow
                        key={index}
                        item={item}
                        columns={columns}
                        className={rowClassName}
                        clickablePath={clickablePath}
                        onRowPress={onRowPress}
                        showHorizontalRowDivider={showHorizontalRowDivider}
                      />
                    ))
                  ) : (
                    <Text className="text-center text-gray-500">
                      {emptyMessage}
                    </Text>
                  )}
                </ScrollView>
              )}
            </View>
          </ScrollView>
        </View>
      )}
    </>
  );
}
